import * as React from 'react';

import { incorrectCount, questionTimeAt } from './helpFlags';
import { HelpRule, LiveViewRow } from './interfaces';

import { Tooltip, pluralize } from '../../../Shared/index';

export const LOW_SCORE_LABEL = 'Low Score';
export const LONG_TIME_LABEL = 'Long Time on Question';
export const IN_PROGRESS_LABEL = 'In Progress';
export const COMPLETED_LABEL = 'Activity Completed';

// Tag tooltips, short and naming the limit in effect for the row's activity
// type (including this browser's May Need Help settings). The general wording
// is the fallback when a row has no rule.
export const LOW_SCORE_DESCRIPTION = 'This student has answered several questions incorrectly.';
export const LONG_TIME_DESCRIPTION = 'This student has spent a long time on their current question.';

export const lowScoreDescription = (rule?: HelpRule|null) => (
  rule?.minFailed ? `This student has ${rule.minFailed} or more questions incorrect.` : LOW_SCORE_DESCRIPTION
);

export const longTimeDescription = (rule?: HelpRule|null) => {
  if (!rule?.questionSeconds) { return LONG_TIME_DESCRIPTION; }

  const minutes = rule.questionSeconds / 60;
  return `This student has spent more than ${minutes} ${pluralize(minutes, 'minute', 'minutes')} on their current question.`;
};

export const COMPLETED_DESCRIPTION = 'This student just completed this activity.';
export const IN_PROGRESS_DESCRIPTION = 'Diagnostic activities are an assessment without feedback, so we do not flag them for May Need Help.';

// The boxes at the top of the report; thresholds vary by activity type, so
// both are described generally there
export const LOW_SCORE_KEY_DESCRIPTION = 'The student is flagged for scoring 0% on one or more questions (based on tool).';
export const LONG_TIME_KEY_DESCRIPTION = 'The student is flagged for how long they have spent on their current question (based on tool).';

// The Long Time tag says how long, in whole minutes on the current question
export const longTimeTagLabel = (row: Pick<LiveViewRow, 'questionTimeSeconds'|'questionTimeAsOf'|'idle'>, now: Date): string => {
  const seconds = questionTimeAt(row, now);
  if (seconds === null) { return LONG_TIME_LABEL; }

  const minutes = Math.floor(seconds / 60);
  return `${LONG_TIME_LABEL}: ${minutes} ${pluralize(minutes, 'Min', 'Mins')}`;
};

export const lowScoreTagLabel = (row: Pick<LiveViewRow, 'prompts'>): string => {
  const count = incorrectCount(row);
  return `${LOW_SCORE_LABEL}: ${count} ${pluralize(count, 'Question', 'Questions')} Incorrect`;
};

// Highest priority first: a student flagged for both shows only Low Score
const REASONS = [
  { key: 'low-score', flag: 'lowScore', label: LOW_SCORE_LABEL, describe: lowScoreDescription, keyDescription: LOW_SCORE_KEY_DESCRIPTION },
  { key: 'long-time', flag: 'tooLong', label: LONG_TIME_LABEL, describe: longTimeDescription, keyDescription: LONG_TIME_KEY_DESCRIPTION },
] as const;

const HelpTag = ({ className, description, label }: { className: string, description: string, label: string }) => (
  <Tooltip
    tooltipText={description}
    tooltipTriggerText={<span className={`may-need-help-tag ${className}`}>{label}</span>}
  />
);

type TagRow = Pick<LiveViewRow, 'lowScore'|'tooLong'|'unflaggedReason'|'helpRule'|'questionTimeSeconds'|'questionTimeAsOf'|'idle'|'prompts'|'completed'>;

const reasonFor = (row: TagRow) => REASONS.find(candidate => row[candidate.flag]);

export const MayNeedHelpTag = ({ row, now = new Date() }: { row: TagRow, now?: Date }) => {
  if (row.completed) {
    return <HelpTag className="completed" description={COMPLETED_DESCRIPTION} label={COMPLETED_LABEL} />;
  }

  if (row.unflaggedReason === 'assessment') {
    return <HelpTag className="in-progress" description={IN_PROGRESS_DESCRIPTION} label={IN_PROGRESS_LABEL} />;
  }

  const reason = reasonFor(row);
  if (!reason) { return null; }

  const label = reason.flag === 'tooLong' ? longTimeTagLabel(row, now) : lowScoreTagLabel(row);
  return <HelpTag className={reason.key} description={reason.describe(row.helpRule)} label={label} />;
};

// Each box's students in the order they were flagged: a newly flagged student
// joins the end, and one no longer flagged drops out without reordering the rest
const useFlaggedOrder = (rows: LiveViewRow[]): Record<string, LiveViewRow[]> => {
  const orderRef = React.useRef<Record<string, string[]>>({});
  const byUid = new Map(rows.map(row => [row.activitySessionUid, row]));

  return Object.fromEntries(REASONS.map(({ key, flag }) => {
    const flagged = new Set(rows.filter(row => row[flag]).map(row => row.activitySessionUid));
    const kept = (orderRef.current[key] || []).filter(uid => flagged.has(uid));
    const order = [...kept, ...[...flagged].filter(uid => !kept.includes(uid))];
    orderRef.current[key] = order;
    return [key, order.map(uid => byUid.get(uid))];
  }));
};

export const LOCKED_STUDENT_LABEL = 'School Premium Shows Full Student List';

const LockedStudent = () => <li className="may-need-help-student locked">{LOCKED_STUDENT_LABEL}</li>;

interface MayNeedHelpKeyProps {
  rows?: LiveViewRow[],
  // Adds a School Premium pointer to each box while students are in activities
  locked?: boolean,
}

// Explains the two tags at the top of the report and lists who has each
export const MayNeedHelpKey = ({ rows = [], locked = false }: MayNeedHelpKeyProps) => {
  const flagged = useFlaggedOrder(rows);
  const showLocked = locked && rows.length > 0;

  return (
    <ul aria-label="May Need Help tags" className="may-need-help-key">
      {REASONS.map(reason => (
        <li className={`may-need-help-key-item ${reason.key}`} key={reason.key}>
          <p className="may-need-help-key-label">May Need Help: {reason.label}</p>
          <p>{reason.keyDescription}</p>
          {(flagged[reason.key].length > 0 || showLocked) && (
            <ul aria-label={`Students flagged for ${reason.label}`} className="may-need-help-students">
              {flagged[reason.key].map(row => <li className="may-need-help-student" key={row.activitySessionUid} title={row.studentName}>{row.studentName}</li>)}
              {showLocked && <LockedStudent />}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};
